import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePage } from '@inertiajs/react';

import { AnnotatedPlainText } from '@/components/blocks/editor/proposal-diff/annotations';
import { RichTextEditor } from '@/components/editor/rich-text-editor';
import { BlockSectionSurface } from '@/components/page-sections/block-section-surface';
import { usePageSectionData } from '@/components/page-sections/page-section-context';
import { BreadcrumbsComponent } from '@/components/ui/breadcrumb';
import { Heading1 } from '@/components/ui/typography';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import {
  BLOCK_STACK_SPACING_CLASSNAMES,
  BLOCK_STACK_VERTICAL_PADDING_NONE,
  type BlockStackAlign,
  resolveBlockStackLayout,
} from '@/lib/blocks/layout/block-stack-layout';
import { resolveTextBlockContent } from '@/lib/blocks/text-content';
import { cmsPagesPreviewPath } from '@/lib/routes';
import { useSectionFieldDiff } from '@/lib/section-proposal-diff/section-review-context';
import { isPlainTextFieldDiff } from '@/lib/section-proposal-diff/types';
import { cn, getDocument, normalizeRootRelativePath, parseUrl } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { BasePageProps, PageBreadcrumb } from '@/types/page';
import type { SharedData } from '@/types/shared';
import { usePageSectionContext } from '../page-section-context';
import { DEFAULT_ELEMENT_ORDER, type PageTitleProps } from './definition';

function resolveDisplayTitle(sectionTitle: string | null, pageTitle?: string): string {
  if (sectionTitle?.trim()) {
    return sectionTitle.trim();
  }

  return pageTitle ?? '';
}

const BLOCK_ALIGN_CLASSES: Record<BlockStackAlign, string> = {
  start: 'items-start text-left',
  center: 'items-center text-center',
  end: 'items-end text-right',
};

const BREADCRUMB_JUSTIFY_CLASSES: Record<BlockStackAlign, string> = {
  start: '',
  center: '[&_ol]:justify-center',
  end: '[&_ol]:justify-end',
};

const TITLE_CLASS = 'w-full scroll-m-20 text-h1 font-extrabold tracking-tight';
const SUBTITLE_CLASS = 'w-full leading-normal';
const SUBTITLE_EDITOR_CLASS =
  'block min-h-8 [&_p]:m-0 [&_.is-editor-empty::before]:!text-current [&_.is-editor-empty::before]:opacity-50';

const EDITABLE_CLASS =
  'outline-none rounded-sm hover:bg-primary/4 focus:bg-primary/6 cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-current/45 empty:before:pointer-events-none';

export function PageTitleSection() {
  const { props } = usePageSectionData<PageTitleProps>();
  const storePage = usePageEditorStore((state) => state.page);
  const { page: pageProp } = usePage<SharedData & BasePageProps>().props;
  const page = storePage && (!pageProp || storePage.id === pageProp.id) ? storePage : pageProp;
  const updateSection = usePageEditorStore((state) => state.updateSection);
  const sectionContext = usePageSectionContext();
  const isPageEditor = useIsPageEditor();
  const isPreview = useIsPreviewMode();
  const isStaticPreview = sectionContext ? sectionContext.renderMode !== 'default' : false;
  const sectionId = sectionContext?.sectionId;
  const titleEditable = Boolean(isPageEditor && !isStaticPreview && sectionId);
  const titleDiff = useSectionFieldDiff('props.title');
  const subtitleDiff = useSectionFieldDiff('props.subtitle');
  const titlePlainTextDiff = titleDiff && isPlainTextFieldDiff(titleDiff) ? titleDiff : null;
  const subtitlePlainTextDiff =
    subtitleDiff && isPlainTextFieldDiff(subtitleDiff) ? subtitleDiff : null;

  const {
    title: sectionTitle = null,
    showBreadcrumb = true,
    showSubtitle = true,
    subtitle = null,
    blockAlign = 'start',
    elementOrder = DEFAULT_ELEMENT_ORDER,
    backgroundSpacing = 'md',
  } = props;

  const displayTitle = resolveDisplayTitle(sectionTitle, page?.title);

  const h1Ref = useRef<HTMLHeadingElement>(null);
  const displayTitleRef = useRef(displayTitle);
  displayTitleRef.current = displayTitle;

  const h1RefCallback = useCallback((node: HTMLHeadingElement | null) => {
    h1Ref.current = node;
    if (node) node.textContent = displayTitleRef.current;
  }, []);

  const prevPageTitleRef = useRef(page?.title);

  useEffect(() => {
    const prevPageTitle = prevPageTitleRef.current;
    prevPageTitleRef.current = page?.title;

    if (!isPageEditor || isStaticPreview || !sectionId) return;

    // Page Settings owns the canonical title. If it changes outside the section
    // editor, drop any inline override so the section falls back to page.title.
    if (prevPageTitle !== undefined && prevPageTitle !== page?.title && sectionTitle != null) {
      updateSection(sectionId, { props: { title: null } });
    }
  }, [isPageEditor, isStaticPreview, page?.title, sectionId, sectionTitle, updateSection]);

  useEffect(() => {
    if (!titleEditable) return;
    if (
      h1Ref.current &&
      getDocument()?.activeElement !== h1Ref.current &&
      h1Ref.current.textContent !== displayTitle
    ) {
      h1Ref.current.textContent = displayTitle;
    }
  }, [displayTitle, titleEditable]);

  const handleTitleBlur = useCallback(() => {
    if (!sectionId) return;
    const text = h1Ref.current?.textContent?.trim() ?? '';
    if (!text) {
      const fallback = page?.title ?? '';
      if (h1Ref.current) h1Ref.current.textContent = fallback;
      if (sectionTitle !== null) {
        updateSection(sectionId, { props: { title: null } });
      }
      return;
    }
    if (text === displayTitle) return;
    updateSection(sectionId, { props: { title: text } });
  }, [sectionId, sectionTitle, displayTitle, updateSection, page?.title]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLHeadingElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  const subtitleAlignClass =
    blockAlign === 'center' ? 'text-center' : blockAlign === 'end' ? 'text-right' : undefined;

  const breadcrumbs: PageBreadcrumb[] = useMemo(() => {
    return page?.breadcrumbs ?? [];
  }, [page?.breadcrumbs]);

  const alignClass = BLOCK_ALIGN_CLASSES[blockAlign];
  const breadcrumbJustifyClass = BREADCRUMB_JUSTIFY_CLASSES[blockAlign];

  const breadcrumbEl =
    showBreadcrumb && breadcrumbs.length > 0 ? (
      <BreadcrumbsComponent
        key="breadcrumb"
        className={cn(
          'w-full font-medium uppercase leading-normal tracking-[0.08em] text-current',
          breadcrumbJustifyClass,
          '[&_ol]:gap-1.5 [&_li]:gap-1.5 md:[&_ol]:gap-2.5 md:[&_li]:gap-2.5 [&_li]:text-sm',
        )}
        linkClassName={isPageEditor ? 'text-current' : 'text-current hover:text-current/60'}
        pageClassName={cn('text-current', isPageEditor && 'cursor-default')}
        showAllOnMobile
        breadcrumbs={breadcrumbs.map((b, i) => {
          const isLast = i === breadcrumbs.length - 1;
          const label = isPageEditor && isLast ? page?.navigationTitle || displayTitle : b.title;

          let href: string | undefined;
          if (!isPageEditor) {
            if (isPreview && b.pageId) {
              const { searchParams } = parseUrl(getDocument()?.location.href ?? '');
              const currentParams = searchParams.toString();
              href =
                cmsPagesPreviewPath({ id: b.pageId }) + (currentParams ? `?${currentParams}` : '');
            } else {
              href = b.path ? normalizeRootRelativePath(b.path) : '/';
            }
          }

          return { label, href };
        })}
      />
    ) : null;

  const titleEl = (
    <div key="title" className={cn('flex w-full flex-col gap-3', alignClass)}>
      {titlePlainTextDiff ? (
        <Heading1 className={TITLE_CLASS}>
          <AnnotatedPlainText fieldDiff={titlePlainTextDiff} />
        </Heading1>
      ) : titleEditable ? (
        <h1
          ref={h1RefCallback}
          contentEditable
          suppressContentEditableWarning
          aria-label="Page title"
          onBlur={handleTitleBlur}
          onKeyDown={handleTitleKeyDown}
          className={cn(TITLE_CLASS, EDITABLE_CLASS, alignClass)}
        />
      ) : displayTitle ? (
        <Heading1 className={cn(TITLE_CLASS, alignClass)}>{displayTitle}</Heading1>
      ) : null}
    </div>
  );

  const subtitleEl = !showSubtitle ? null : subtitlePlainTextDiff ? (
    <div key="subtitle" className={cn('w-full', SUBTITLE_CLASS, subtitleAlignClass)}>
      <AnnotatedPlainText fieldDiff={subtitlePlainTextDiff} />
    </div>
  ) : (
    <RichTextEditor
      key="subtitle"
      value={resolveTextBlockContent(subtitle ?? '')}
      fieldPosition="subtitle"
      placeholder="Add a subtitle..."
      className={cn(SUBTITLE_CLASS, SUBTITLE_EDITOR_CLASS, subtitleAlignClass)}
    />
  );

  const elementMap = { breadcrumb: breadcrumbEl, title: titleEl, subtitle: subtitleEl };

  const stackLayout = resolveBlockStackLayout(props as unknown as Record<string, unknown>);
  const stackPadding = BLOCK_STACK_SPACING_CLASSNAMES[stackLayout.padding].padding;
  const verticalPaddingNone = backgroundSpacing === 'none';

  return (
    <BlockSectionSurface sectionProps={props as unknown as Record<string, unknown>}>
      <div
        className={cn(
          'flex w-full flex-col gap-3',
          stackPadding,
          verticalPaddingNone && BLOCK_STACK_VERTICAL_PADDING_NONE,
          alignClass,
        )}
      >
        {(elementOrder ?? DEFAULT_ELEMENT_ORDER).map((el) => elementMap[el])}
      </div>
    </BlockSectionSurface>
  );
}
